import React, { useState } from "react";
import { Share2, Link2, Copy, Mail, Download } from "lucide-react";

const SITE = "https://liftedatthemoment.com";

// Share links point at the server-rendered preview page so social crawlers
// see this article's own title, description and photo.
export function shareUrlFor(a) {
  const base = window.location.hostname.includes("localhost") ? SITE : window.location.origin;
  return `${base}/api/news/share/${a.slug}`;
}

export function ShareBar({ article, compact = false }) {
  const [msg, setMsg] = useState("");
  const url = shareUrlFor(article);
  const caption = `${article.headline} — ${article.summary || ""}`.slice(0, 280);
  const enc = encodeURIComponent;
  const links = [
    ["x", "X", `https://x.com/intent/post?text=${enc(article.headline)}&url=${enc(url)}`],
    ["facebook", "Facebook", `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`],
    ["linkedin", "LinkedIn", `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`],
    ["reddit", "Reddit", `https://www.reddit.com/submit?url=${enc(url)}&title=${enc(article.headline)}`],
    ["whatsapp", "WhatsApp", `https://wa.me/?text=${enc(`${article.headline} ${url}`)}`],
  ];
  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); setMsg(`${label} copied to clipboard`); } catch { setMsg("Copy not available in this browser"); }
    setTimeout(() => setMsg(""), 2500);
  };
  const native = async () => {
    try { await navigator.share({ title: article.headline, text: article.summary, url }); } catch { /* user dismissed */ }
  };
  const btn = "inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-full border border-white/15 text-white/80 hover:border-lifted-green hover:text-lifted-green transition-colors";
  const img = article.image;
  return (
    <div className={compact ? "mt-3" : "mt-8 glass-panel p-4"} data-testid={`share-bar-${article.slug}`}>
      {!compact && <div className="eyebrow text-lifted-green mb-2">Share this story</div>}
      <div className="flex flex-wrap gap-1.5">
        {typeof navigator !== "undefined" && navigator.share && (
          <button onClick={native} className={btn} data-testid="share-native"><Share2 className="h-3 w-3" /> Share</button>
        )}
        {links.map(([k, l, href]) => (
          <a key={k} href={href} target="_blank" rel="noopener noreferrer" className={btn} data-testid={`share-${k}`}>{l}</a>
        ))}
        <a href={`mailto:?subject=${enc(article.headline)}&body=${enc(`${article.summary || ""}\n\n${url}`)}`} className={btn} data-testid="share-email"><Mail className="h-3 w-3" /> Email</a>
        <button onClick={() => copy(url, "Link")} className={btn} data-testid="share-copy-link"><Link2 className="h-3 w-3" /> Copy link</button>
        <button onClick={() => copy(`${caption}\n${url}`, "Caption")} className={btn} data-testid="share-copy-caption"><Copy className="h-3 w-3" /> Copy caption</button>
        {!compact && img?.download_ok && img?.source_url && (
          <a href={img.source_url} target="_blank" rel="noopener noreferrer" className={btn} data-testid="share-image-download"><Download className="h-3 w-3" /> Photo (license)</a>
        )}
      </div>
      {msg && <div className="text-[11px] text-lifted-green mt-2" data-testid="share-message">{msg}</div>}
      {!compact && <p className="text-[10px] text-white/40 mt-2">Share buttons open each platform so you can post from your own account. Nothing is posted automatically.</p>}
    </div>
  );
}

export function ArticlePhoto({ image }) {
  if (!image?.url) return null;
  return (
    <figure className="mt-6" data-testid="news-photo">
      <img src={image.url} alt={image.alt} className="w-full aspect-[16/9] object-cover rounded-lg border border-white/10" loading="eager" />
      <figcaption className="text-[11px] text-white/50 mt-2" data-testid="news-photo-credit">
        {image.illustrative ? "Illustrative stock photograph. " : ""}{image.caption && !image.illustrative ? `${image.caption} ` : ""}
        {image.photographer_url
          ? <a href={image.photographer_url} target="_blank" rel="noopener noreferrer" className="underline">{image.credit}</a>
          : image.credit}
        {image.provider === "pexels" && <> · <a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer" className="underline">Pexels</a></>}
      </figcaption>
    </figure>
  );
}

export function ArticleBody({ body }) {
  return (
    <div className="mt-8 space-y-5 text-white/80 leading-relaxed" data-testid="news-detail-body">
      {(body || "").split(/\n{2,}/).map((block, i) => {
        const m = block.match(/^## (.+)\n([\s\S]*)$/);
        if (!m) return <p key={i} className="whitespace-pre-wrap">{block}</p>;
        return (
          <section key={i}>
            <h2 className="font-bebas uppercase tracking-[0.18em] text-lifted-green text-lg">{m[1]}</h2>
            <p className="whitespace-pre-wrap mt-1">{m[2]}</p>
          </section>
        );
      })}
    </div>
  );
}
